import React, { useState } from 'react';
import { VerticalFeed } from 'react-vertical-feed';
import { Info, Bookmark, BookmarkCheck, HeartPlus, Heart } from 'lucide-react';
import BottomMenuBar from './BottomMenuBar';

export interface VideoItem {
  src: string;
  id?: string;
  metadata?: Record<string, unknown>;
  controls?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  // Video description box fields
  descriptionImage?: string;
  title?: string;
  description?: string;
}

export interface VerticalFeedSDKProps {
  items: VideoItem[];
  onEndReached?: () => void;
  onItemVisible?: (item: VideoItem, index: number) => void;
  onItemHidden?: (item: VideoItem, index: number) => void;
  onItemClick?: (item: VideoItem, index: number) => void;
  renderItemOverlay?: (item: VideoItem, index: number) => React.ReactNode;
  threshold?: number;
  scrollBehavior?: ScrollBehavior;
  className?: string;
  style?: React.CSSProperties;
  loadingComponent?: React.ReactNode;
  errorComponent?: React.ReactNode;
}

// Component for action buttons on the right side
const VideoActionButtons: React.FC<{ index: number }> = ({ index }) => {
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isLiked, setIsLiked] = useState(false);

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log(`Info clicked for video index: ${index}`);
  };

  const handleBookmarkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsBookmarked(!isBookmarked);
    console.log(`Bookmark ${!isBookmarked ? 'added' : 'removed'} for video index: ${index}`);
  };

  const handleLikeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLiked(!isLiked);
    console.log(`Like ${!isLiked ? 'added' : 'removed'} for video index: ${index}`);
  };

  return (
    <>
      <style>{`
        .video-action-buttons {
          position: absolute;
          right: 16px;
          bottom: 200px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          z-index: 10;
        }
        .action-button {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(8px);
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .action-button:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: scale(1.1);
        }
        .action-button:active {
          transform: scale(0.95);
        }
        .action-button.active {
          background: rgba(0, 0, 0, 0.8);
        }
        .action-button svg {
          width: 24px;
          height: 24px;
        }
      `}</style>
      <div className="video-action-buttons">
        <button className="action-button" onClick={handleInfoClick}>
          <Info size={24} color="#ffd700" />
        </button>
        <button
          className={`action-button ${isBookmarked ? 'active' : ''}`}
          onClick={handleBookmarkClick}
        >
          {isBookmarked ? (
            <BookmarkCheck size={24} color="#ffd700" />
          ) : (
            <Bookmark size={24} color="rgba(255, 255, 255, 0.7)" />
          )}
        </button>
        <button
          className={`action-button ${isLiked ? 'active' : ''}`}
          onClick={handleLikeClick}
        >
          {isLiked ? (
            <Heart size={24} color="#ffd700" fill="#ffd700" />
          ) : (
            <HeartPlus size={24} color="rgba(255, 255, 255, 0.7)" />
          )}
        </button>
      </div>
    </>
  );
};

const VerticalFeedSDK: React.FC<VerticalFeedSDKProps> = ({
  items,
  onEndReached,
  onItemVisible,
  onItemHidden,
  onItemClick,
  renderItemOverlay,
  threshold = 0.75,
  scrollBehavior = 'smooth',
  className,
  style,
  loadingComponent,
  errorComponent,
}) => {
  // Process items to ensure controls are false by default (unless explicitly set)
  const processedItems: VideoItem[] = items.map((item) => ({
    ...item,
    controls: item.controls ?? false, // Default to false if not specified
    muted: item.muted ?? true, // Default to muted
    playsInline: item.playsInline ?? true, // Default to playsInline
    autoPlay: item.autoPlay ?? true, // Default to autoplay
  }));

  // Component for video description box at the top
  const VideoDescriptionBox: React.FC<{ item: VideoItem }> = ({ item }) => {
    if (!item.title && !item.description && !item.descriptionImage) {
      return null;
    }

    return (
      <>
        <style>{`
          .video-description-box {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            background: rgba(0, 0, 0, 0.6);
            backdrop-filter: blur(10px);
            padding: 12px 16px;
            display: flex;
            align-items: center;
            gap: 12px;
            z-index: 10;
          }
          .description-image {
            width: 48px;
            height: 48px;
            border-radius: 20%;
            object-fit: cover;
            flex-shrink: 0;
            border: 2px solid rgba(255, 255, 255, 0.2);
          }
          .description-content {
            flex: 1;
            min-width: 0;
          }
          .description-title {
            font-size: 16px;
            font-weight: 700;
            color: #ffffff;
            margin: 0 0 4px 0;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .description-text {
            font-size: 12px;
            color: rgba(255, 255, 255, 0.7);
            margin: 0;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        `}</style>
        <div className="video-description-box">
          {item.descriptionImage && (
            <img
              src={item.descriptionImage}
              alt={item.title || 'Video'}
              className="description-image"
            />
          )}
          <div className="description-content">
            {item.title && <div className="description-title">{item.title}</div>}
            {item.description && <div className="description-text">{item.description}</div>}
          </div>
        </div>
      </>
    );
  };

  // Default overlay with PLAY NOW CTA if no custom overlay provided
  const defaultRenderOverlay = (item: VideoItem, index: number) => {
    const handlePlayClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      console.log(`Play now clicked for video index: ${index}`);
    };

    return (
      <>
        <style>{`
          .play-now-cta {
            position: absolute;
            bottom: 100px;
            left: 50%;
            transform: translateX(-50%);
            width: 80vw;
            max-width: 400px;
            padding: 16px 32px;
            background: #ffd700;
            color: #0a0a12;
            border: none;
            border-radius: 50px;
            font-size: 16px;
            font-weight: 700;
            cursor: pointer;
            text-transform: uppercase;
            letter-spacing: 1px;
            transition: all 0.3s ease;
            z-index: 10;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
          }
          .play-now-cta:hover {
            background: #ffed4e;
            transform: translateX(-50%) scale(1.05);
          }
          .play-now-cta:active {
            transform: translateX(-50%) scale(0.98);
          }
        `}</style>
        <VideoDescriptionBox item={item} />
        <VideoActionButtons index={index} />
        <button className="play-now-cta" onClick={handlePlayClick}>
          Play Now
        </button>
      </>
    );
  };

  // Self-contained styles for fullscreen, scrollbar hiding, etc.
  // Note: Don't set overflow here - let VerticalFeed component handle its own scrolling
  const sdkStyles: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    margin: 0,
    padding: 0,
    zIndex: 9999,
    backgroundColor: '#0a0a12',
    ...style,
  };

  const sdkClassName = `vertical-feed-sdk-container ${className || ''}`.trim();

  // Feed container styles with padding-bottom for menu bar
  const feedContainerStyles: React.CSSProperties = {
    ...sdkStyles,
    paddingBottom: '70px', // Space for bottom menu bar
  };

  return (
    <>
      <style>{`
        /* Hide scrollbars on the SDK container and its children */
        .vertical-feed-sdk-container {
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE and Edge */
        }
        .vertical-feed-sdk-container::-webkit-scrollbar {
          display: none; /* Chrome, Safari, Opera */
        }
        /* Hide scrollbars on any nested scrollable elements */
        .vertical-feed-sdk-container * {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .vertical-feed-sdk-container *::-webkit-scrollbar {
          display: none;
        }
        /* Prevent body/html scrolling when SDK is active */
        body.vertical-feed-sdk-active {
          overflow: hidden;
          margin: 0;
          padding: 0;
          height: 100vh;
          width: 100vw;
          position: fixed;
        }
        html.vertical-feed-sdk-active {
          overflow: hidden;
          margin: 0;
          padding: 0;
          height: 100%;
          width: 100%;
        }
        /* Ensure feed content doesn't go under menu bar */
        .vertical-feed-sdk-wrapper {
          position: relative;
          width: 100%;
          height: 100%;
        }
      `}</style>
      <div className="vertical-feed-sdk-wrapper" style={sdkStyles}>
        <VerticalFeed
          items={processedItems}
          onEndReached={onEndReached}
          onItemVisible={onItemVisible}
          onItemHidden={onItemHidden}
          onItemClick={onItemClick}
          renderItemOverlay={renderItemOverlay || defaultRenderOverlay}
          threshold={threshold}
          scrollBehavior={scrollBehavior}
          className={sdkClassName}
          style={feedContainerStyles}
          loadingComponent={loadingComponent}
          errorComponent={errorComponent}
        />
        <BottomMenuBar />
      </div>
    </>
  );
};

export default VerticalFeedSDK;
